import React, { useState, useRef } from 'react';
import moment from 'moment';
import { Row, Col, Button } from 'reactstrap';
import ReactTooltip from 'react-tooltip';
import fileDownload from 'js-file-download';
import fields from './exportFields';

import employeeTableMapping from './employeeTableMapping';
import ModalManageEmployee from './ModalManageEmployee';
import ModalProvidePassword from './ModalProvidePassword';
import { updateLocalValue, isChoiceEmail, notify, getTsDate } from '../../utils';
import _ from 'lodash';
import useExistingEmployees from './useExistingEmployees';
import { logAliasMap } from './logAliasMap';
import apiClient from '../../apiClient';
import VirtualTable from '../custom/VirtualTable';
import useJobTitles from '../../views/hr/HrSettings/JobTitles/useJobTitles';
import useEmployees from './useEmployees';

export default function Employees ({
    user,
    units,
    employees, 
    setEmployees,
    accessLevels,
    getAirlines,
    airlines,
    setLoading,
    nativeAccessMapSchema
}) {    

    const existingEmployeeData = useExistingEmployees(employees);
    const { data: jobTitles, jobTitlesMap } = useJobTitles();
    const enhancedEmployees = useEmployees(employees, jobTitlesMap);
    const [modalManageEmployee, setModalManageEmployee] = useState(false);
    const [newEmployee, setNewEmployee] = useState(false);
    const [modalPassword, setModalPassword] = useState(false);
    const [createdEmployee, setCreatedEmployee] = useState({});
    const [selectedEmployee, setSelectedEmployee] = useState({});
    const [tableKey, setTableKey] = useState(0);
    const reactTableRef = useRef();

    const handleNewEmployee = () => {
        setNewEmployee(true);
        setModalManageEmployee(true);
    }

    const handleUpdateEmployee = (employee) => {
        setNewEmployee(false);
        setSelectedEmployee(employee);
        setModalManageEmployee(true);
    }

    const resolveAirlines = (map) => {
        let string = '';
        for (let key in map) {
            string += `${key},`;
        }
        return string;
    }

    const offsetDateValue = (value) => {
        if (moment(value).isValid()) {
            return moment(value).utc().add(1, 'day').format('YYYY-MM-DD');
        }
        // if (moment(value).isValid() && !moment().isDST()) {
        //     alert('working');
        //     value = moment(value).add(1, 'day');
        //     return value;
        // } else {
        //     return null;
        // }
    }

    const getJobTitleString = (i_job_id) => {
        const match = jobTitles.find(item => item.id == i_job_id);
        return match ? match.title : null;
    }

    const addChoiceEmployee = async (values, selectedAirlines, employeeLog, resetForm) => {
        setLoading(true);

        const useValues = _.cloneDeep(values);
        // Offset date values by +1 day if NOT daylight savings:
        // useValues.d_hire = offsetDateValue(useValues.d_hire);

        const now = moment().local().format('MM/DD/YYYY HH:mm:ss');
        const email = user && user.s_email;

        const data = useValues;
        
        data.t_created = now;
        data.s_created_by = email;
        data.t_modified = now;
        data.s_modified_by = email;
        data.b_internal = isChoiceEmail(useValues.s_email) ? true : false;
        data.b_airline = data.b_airline ? true : false;
        data.JOB_ROLE_1 = getJobTitleString(values.i_job_id);

        data.s_airline_codes = resolveAirlines(selectedAirlines);
        data.employeeLog = employeeLog;

        try {
            const response = await apiClient.post('/addChoiceEmployee', { data });

            if (response.status === 200) {
                const createdEmployee = response.data;
                setEmployees(prev => {
                    const copy = _.cloneDeep(prev);
                    copy.push(createdEmployee);
                    return copy;
                })
                setTableKey(tableKey + 1);
                notify('Employee Added');

                setCreatedEmployee(createdEmployee);
                setSelectedEmployee(createdEmployee);
                setNewEmployee(false);
            }
        } catch (err) {
            const message = _.get(err, 'response.data.name', 'Error creating employee');
            notify(message, 'error');
        }
        setLoading(false);
    }

    const updateChoiceEmployee = async (values, selectedAirlines, employeeLog, resetForm) => {
        setLoading(true);

        const useValues = _.cloneDeep(values);
        // Offset date values by +1 day if NOT daylight savings:
        // useValues.d_hire = offsetDateValue(useValues.d_hire);

        const employee = values;
        employee.id = selectedEmployee.id;
        employee.s_modified_by = user.s_email;
        employee.t_modified = getTsDate();
        employee.JOB_ROLE_1 = getJobTitleString(values.i_job_id);

        const census = {
            s_type: values.s_status,
            s_email: values.s_email,
        }

        if (values.s_status === 'TERMINATED') {
            employee.s_ip_subnet = null;
            census.d_terminated = values.d_terminated;
        } else if (values.s_status === 'SUSPENDED') {
            employee.s_ip_subnet = null;
            census.d_suspended_start = values.d_suspended_start;
            census.d_suspended_end = values.d_suspended_end;
        } else if (values.s_status === 'FURLOUGHED') {
            census.d_furlough_start = values.d_furlough_start;
            census.d_furlough_end = values.d_furlough_end;
        }

        employee.b_internal = isChoiceEmail(values.s_email) ? true : false;
        employee.b_airline = employee.b_airline ? true : false;
        employee.s_airline_codes = resolveAirlines(selectedAirlines);
        delete values.d_terminated;

        const data = {
            employee,
            census,
            employeeLog
        }

        try {
            const response = await apiClient.put('/updateChoiceEmployee', { data });

            if (response.status === 200) {
                notify('Employee updated');
                setModalManageEmployee(false);
                setEmployees(prev => {
                    const copy = _.cloneDeep(prev);
                    for (let i = 0; i < copy.length; i++) {
                        if (copy[i].id === response.data.id) {
                            copy[i] = response.data;
                            break;
                        }
                    }
                    return copy;
                });
                resetForm();
                setTableKey(tableKey + 1);
            }
        } catch (err) {
            alert(err);
        }
        setLoading(false);
    }

    const deleteEmployee = async (s_email) => {
        try {
            await apiClient.post(`/deleteEmployee`, {
                s_email,
                changes: `EMPLOYEE DELETED: ${selectedEmployee.s_first_name} ${selectedEmployee.s_last_name} (${selectedEmployee.s_job_title} at ${selectedEmployee.s_unit})`,
                created: getTsDate(),
                createdBy: user.s_email
            });
            setEmployees(prev => {
                const filtered = prev.filter(e => e.s_email !== s_email);
                return filtered;
            });
            setModalManageEmployee(false);
            notify('Employee Deleted');
        } catch (err) {
            alert(err);
        }
    }

    const generateEmployeeLog = (initialValues, currentValues) => {
        // Start building the changes string
        let changesString = 'Changes made to the following fields - ';
        let changes = false;

        // Iterate over each field
        for (let fieldName in initialValues) {
            // If the field's value has been changed
            if (initialValues[fieldName] !== currentValues[fieldName]) {
                changes = true;
                // Add to the changes string
                const fieldAlias = logAliasMap[fieldName] || fieldName;
                const falseValue = typeof currentValues[fieldName] === 'boolean' && currentValues[fieldName] === false;
                const newValue = falseValue ? 'false' : currentValues[fieldName] || ''.toUpperCase();
                changesString += `${fieldAlias}: ${initialValues[fieldName]} -> ${newValue}; `;
            }
        }

        // Trim the trailing comma and space
        if (changesString.endsWith(', ')) {
            changesString = changesString.substring(0, changesString.length - 2);
        }

        return changes ? changesString : null;
    }

    const handleAddEditEmployee = (initialValues, values, selectedAirlines, resetForm) => {
        const employeeLog = generateEmployeeLog(initialValues, values);

        if (newEmployee) {
            addChoiceEmployee(values, selectedAirlines, employeeLog, resetForm);
        } else {
            updateChoiceEmployee(values, selectedAirlines, employeeLog, resetForm);
        }
    }

    const formatDateTime = (date) => moment(date).format('MM/DD/YYYY HH:mm:ss');

    const exportHrData = () => {
        const Json2csvParser = require("json2csv").Parser;
        const raw = reactTableRef.current.getResolvedState().sortedData;

        if (raw.length > 0) {
            const data = raw.map(record => {
            
                // Delete AccessLevel property
                delete record._original.AccessLevel;
                
                // Offset id by -1
                record._original.id--;
    
                // Format Dates correctly
                const formatDates = ['t_created', 't_modified'];
    
                for (let i = 0; i < formatDates.length; i++) {
                    record._original[formatDates[i]] = formatDateTime(record._original[formatDates[i]]);
                }
    
                return record._original;
            });
            const jsonData = JSON.parse(JSON.stringify(data));
            const json2csvParser = new Json2csvParser({ excelStrings: true, withBOM: true, fields});
            const csv = json2csvParser.parse(jsonData);
            fileDownload(csv, `CHOICE Employees Report ${moment().format('YYYY-MM-DD')}.csv`);
        } else {
            notify('No records to download.', 'warning');
        }

    }

    return (
        <Row>
            <ReactTooltip />
            <Col md={12} className='mb-2'>
                <i 
                    className="fas fa-plus-circle text-primary float-left hover-pointer"
                    style={{ fontSize: '24px' }} 
                    onClick={() => handleNewEmployee()}
                    data-tip={'Add New Employee'} 
                ></i>
                <i 
                    className="fas fa-file-download text-primary float-right hover-pointer" 
                    style={{ fontSize: '24px' }} 
                    onClick={() => exportHrData()}
                    data-tip={'Download data'} 
                ></i>
            </Col>
            <Col md={12}>
                <VirtualTable
                    data={enhancedEmployees}
                    index={true}
                    mapping={employeeTableMapping}
                    enableClick={true}
                    handleClick={handleUpdateEmployee}
                    reactTableRef={reactTableRef}
                />
            </Col>

            <ModalManageEmployee 
                user={user}
                units={units}
                modal={modalManageEmployee}
                setModal={setModalManageEmployee}
                newEmployee={newEmployee}
                handleAddEditEmployee={handleAddEditEmployee}
                selectedEmployee={selectedEmployee}
                setSelectedEmployee={setSelectedEmployee}
                accessLevels={accessLevels}
                getAirlines={getAirlines}
                airlines={airlines}
                existingEmployeeData={existingEmployeeData}
                nativeAccessMapSchema={nativeAccessMapSchema}
                deleteEmployee={deleteEmployee}
                jobTitles={jobTitles}
            />

            <ModalProvidePassword 
                modal={modalPassword}
                setModal={setModalPassword}
                employee={createdEmployee}
            />
        </Row>
    )
}